// client/src/components/dashboard/properties/ViewSharedProperty.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext'; // Import useAuth
import LoadingSpinner from '../../common/LoadingSpinner.js';
import { useTranslation } from 'react-i18next';
import { FaTrashAlt } from 'react-icons/fa';
import { leaveShare } from '../../../api/authApi';

import './AddPropertyForm.css';

// --- Data structures for dropdowns (needed for display) ---
const propertyHierarchy = {
    residential: [{ value: 'house', labelKey: 'properties.type.house', defaultLabel: 'House' }, /* ... */],
    agricultural: [{ value: 'farm', labelKey: 'properties.type.farm', defaultLabel: 'Farm' }, /* ... */],
    commercial: [{ value: 'office', labelKey: 'properties.type.office', defaultLabel: 'Office' }, /* ... */],
    industrial: [{ value: 'factory', labelKey: 'properties.type.factory', defaultLabel: 'Factory' }, /* ... */],
    outdoor: [{ value: 'garden', labelKey: 'properties.type.garden', defaultLabel: 'Garden' }, /* ... */],
    other: [{ value: 'other', labelKey: 'properties.type.other', defaultLabel: 'Other' }]
};
const categoryOptions = [
    { value: 'residential', labelKey: 'properties.category.residential', defaultLabel: 'Residential' },
    { value: 'agricultural', labelKey: 'properties.category.agricultural', defaultLabel: 'Agricultural' },
    { value: 'commercial', labelKey: 'properties.category.commercial', defaultLabel: 'Commercial' },
    { value: 'industrial', labelKey: 'properties.category.industrial', defaultLabel: 'Industrial' },
    { value: 'outdoor', labelKey: 'properties.category.outdoor', defaultLabel: 'Outdoor/Utility' },
    { value: 'other', labelKey: 'properties.category.other', defaultLabel: 'Other' },
];
// --- End Data Structures ---


function ViewSharedProperty({ propertyData, onLeaveSuccess, onCancel }) {
    const { t } = useTranslation();
    const { user } = useAuth();

    // Display data derived from propertyData prop
    const [displayData, setDisplayData] = useState({});
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false); // Loading state for Leave action

    // Effect to populate display data when propertyData changes
    useEffect(() => {
        if (propertyData) {
            setDisplayData({
                propertyName: propertyData.property_name || '',
                propertyCategory: propertyData.property_category || '',
                propertyType: propertyData.property_type || '',
                addressStreet: propertyData.address_street || '',
                addressCity: propertyData.address_city || '',
                addressState: propertyData.address_state || '',
                addressPostalCode: propertyData.address_postal_code || '',
                addressCountry: propertyData.address_country || '',
                // --- Extract Owner Details ---
                ownerUsername: propertyData.owner_username || 'Unknown Owner',
                ownerEmailPrimary: propertyData.owner_email_primary || '', // Get owner email
                ownerPhonePrimary: propertyData.owner_phone_primary || ''   // Get owner phone
            });
            setError(''); // Clear error when new data is loaded
        } else {
            setDisplayData({}); // Clear data if prop becomes null
            setError('');
        }
    }, [propertyData]);

    // Find display labels for category and type
    const categoryLabel = categoryOptions.find(opt => opt.value === displayData.propertyCategory)?.labelKey;
    const typeLabel = propertyHierarchy[displayData.propertyCategory]?.find(opt => opt.value === displayData.propertyType)?.labelKey;


    // --- Handle LEAVE SHARE Click ---
    const handleLeaveShareClick = async () => {
        setError('');
        // Add translation key
        
        setLoading(true);
        try {
            console.log(`Attempting to leave share for property ID ${propertyData.property_id}`);
            // Use mock/real API - needs property ID
            await leaveShare(propertyData.property_id);
            onLeaveSuccess(); // Call parent callback to hide view and refresh list
        } catch (err) {
            console.error("Error leaving share:", err);
            setError(err.message || t('errors.api.requestFailed'));
        } finally {
            setLoading(false);
        }
    };
    if (!user) {
        return <LoadingSpinner />; // Use the LoadingSpinner
    }

    // Render the read-only form
    // Using disabled form elements for consistent styling, could also use <p> tags
    return (
        <div className="view-property-form add-property-form"> {/* Reuse styles */}
            {/* Use specific title key */}
            <h2>{t('properties.viewSharedTitle', 'Shared Property Details')}</h2>
            {/* --- Display Owner Info --- */}
            <div className="shared-owner-info-box"> {/* Added wrapper */}
                <p className="shared-owner-details-inline">
                    <span>{t('properties.sharedBy', 'Shared by')}: <strong>{displayData.ownerUsername}</strong></span>
                    {/* Conditionally add email */}
                    {displayData.ownerEmailPrimary && (
                        <span className="owner-contact-detail">
                            {' - '}{t('properties.ownerEmailShort', 'Email')}: {displayData.ownerEmailPrimary}
                        </span>
                    )}
                    {/* Conditionally add phone */}
                    {displayData.ownerPhonePrimary && (
                        <span className="owner-contact-detail">
                            {' - '}{t('properties.ownerPhoneShort', 'Phone')}: {displayData.ownerPhonePrimary}
                        </span>
                    )}
                </p>
            </div>
            {/* --- End Owner Info --- */}

            <hr /> {/* Separator */}
            {error && <div className="form-error">{error}</div>}
            <form> {/* Form tag for structure, but no submit handler needed */}
                <div className="form-row-3-column">
                    {/* Property Name */}
                    <div className="form-group">
                        <label htmlFor="viewPropertyName">{t('properties.nameLabel', 'Property Name')}:</label>
                        <input type="text" id="viewPropertyName" value={displayData.propertyName} disabled />
                    </div>

                    {/* Property Category */}
                    <div className="form-group">
                        <label htmlFor="viewPropertyCategory">{t('properties.categoryLabel', 'Property Category')}:</label>
                        {/* Display translated text or value */}
                        <input type="text" id="viewPropertyCategory" value={t(categoryLabel, displayData.propertyCategory || 'N/A')} disabled />
                    </div>

                    {/* Property Type */}
                    <div className="form-group">
                        <label htmlFor="viewPropertyType">{t('properties.typeLabel', 'Property Type')}:</label>
                        <input type="text" id="viewPropertyType" value={t(typeLabel, displayData.propertyType || 'N/A')} disabled />
                    </div>
                </div>
                {/* Address Fields */}
                <div className="form-group">
                    <label htmlFor="viewAddressStreet">{t('properties.addressStreetLabel', 'Street Address')}:</label>
                    <input type="text" id="viewAddressStreet" value={displayData.addressStreet || 'N/A'} disabled />
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="viewAddressCity">{t('properties.addressCityLabel', 'City')}:</label>
                        <input type="text" id="viewAddressCity" value={displayData.addressCity || 'N/A'} disabled />
                    </div>
                    <div className="form-group">
                        <label htmlFor="viewAddressState">{t('properties.addressStateLabel', 'State/Province')}:</label>
                        <input type="text" id="viewAddressState" value={displayData.addressState || 'N/A'} disabled />
                    </div>
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="viewAddressPostalCode">{t('properties.addressPostalCodeLabel', 'Postal Code')}:</label>
                        <input type="text" id="viewAddressPostalCode" value={displayData.addressPostalCode || 'N/A'} disabled />
                    </div>
                    <div className="form-group">
                        <label htmlFor="viewAddressCountry">{t('properties.addressCountryLabel', 'Country')}:</label>
                        <input type="text" id="viewAddressCountry" value={t(`countries.${displayData.addressCountry}`, displayData.addressCountry || 'N/A')} disabled />
                    </div>
                </div>

                {/* Action Buttons: Leave Share, Close */}
                <div className="form-actions">
                    {/* Leave Share Button */}
                    <button type="button" className="form-action-button delete-button" onClick={handleLeaveShareClick} disabled={loading}>
                        {loading ? '...' : <FaTrashAlt />} {t('properties.leaveShare', 'Leave Share')} {/* Add key */}
                    </button>
                    {/* Close Button */}
                    <button type="button" className="form-action-button cancel-button" onClick={onCancel} disabled={loading}>
                        {t('common.close', 'Close')}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default ViewSharedProperty;